import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiLayers, FiCornerUpLeft, FiDownload, FiUpload, FiCopy, FiFilePlus } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import SQLEditor from '../SQLEditor';
import SqlOutline, { queryTitle } from './SqlOutline';
import { parseSqlStructure, walk } from '../../utils/sqlStructure/parse';
import { addClause, addScalar, addInline, addCondition, addCte, addUnion, removeNode } from '../../utils/sqlStructure/edits';
import './SqlBuilder.css';
import memoWindow from '../common/memoWindow';
import { listenDrag } from '../../utils/dragListen';
import { toCssPx } from '../../utils/appZoom';
import EditorConnectionChip from '../common/EditorConnectionChip';

/**
 * SQL 빌더(2026-10-19) — 메인쿼리와 여러 서브쿼리가 섞인 SQL 을 요소 단위로 보고·고치고·조립한다.
 *
 * **원본은 SQL 텍스트 하나다**(sql 상태). 왼쪽 아웃라인은 그것을 파싱한 결과를 그리기만 하고, 편집은 전부
 * 「원문의 범위를 바꾸고 → 다시 파싱」 한 길이다 — 왼쪽에서 요소를 넣든(형식 텍스트를 그 자리에) 오른쪽에서 서브쿼리를 쓰든.
 * 트리와 텍스트를 따로 들고 맞추면 동기화가 끊긴다(dq 노드 그래프 빌더의 실패, 2026-10-19 조사).
 *
 * 오른쪽은 기본이 「전체」 다(2026-10-27 사용자 요청 — 예전엔 「선택 요소만」). 「선택 요소만」 은 고른 질의·절의 텍스트만 편집하고 원래 자리에 다시 끼운다(그 범위는 focusRange 로
 * 직접 따라간다: 타자마다 다시 파싱한 범위를 쓰면 그 사이 구조가 바뀌어 엉뚱한 자리에 끼울 수 있다). 「전체」 는 전체 SQL 에서 그 범위를 강조한다.
 */

function indexTree(root) {
  const map = new Map();
  walk(root, (n, type, depth, parent) => { map.set(n.id, { node: n, type, depth, parent }); });
  return map;
}

/** 오프셋에서 시작하는 요소(추가 직후 고를 것) — 질의(괄호·본문 시작) → 절 → 그 자리를 품은 가장 깊은 질의 */
function nodeStartingAt(index, offset) {
  let best = null;
  for (const info of index.values()) {
    const n = info.node;
    if (info.type === 'query' && (n.outerStart === offset || n.start === offset) && (!best || info.depth > best.depth)) best = info;
  }
  if (best) return best;
  for (const info of index.values()) if (info.type === 'clause' && info.node.start === offset) return info;
  let deep = null;
  for (const info of index.values()) {
    const n = info.node;
    if (info.type === 'query' && offset >= n.outerStart && offset <= n.outerEnd && (!deep || info.depth >= deep.depth)) deep = info;
  }
  return deep;
}

const LEFT_W_KEY = 'livesql.sqlBuilder.leftWidth';
const LEFT_MIN = 240;
const readLeftWidth = () => {
  try { const v = Number(localStorage.getItem(LEFT_W_KEY)); return v >= LEFT_MIN ? v : 400; } catch { return 400; }
};

const rangeOf = (info) => (info ? { start: info.node.start, end: info.node.end } : null);
const NOOP = () => {};

// connectionId 는 SQL 편집 탭의 연결이다 — 빌더는 고르지 않는다(반영한 SQL 이 그 탭의 세션에서 실행된다, 제목줄 칩은 표시만)
function SqlBuilderModal({ open, onClose, seed, onSeedUsed, onImport, onApply, onNewTab, schemas, connections, connectionId, dbType }) {
  const { t } = useTranslation();
  const [sql, setSql] = useState('');
  const [selectedId, setSelectedId] = useState('q0');
  const [mode, setMode] = useState('full');             // full = 전체(기본, 2026-10-27 사용자 요청), focus = 선택 요소만
  const [leftWidth, setLeftWidth] = useState(readLeftWidth);
  const [splitDrag, setSplitDrag] = useState(false);
  const bodyRef = useRef(null);
  const [focusRange, setFocusRange] = useState(null);
  const [highlight, setHighlight] = useState([]);
  const [history, setHistory] = useState([]);
  const pendingFocus = useRef(null);
  // 왼쪽에서 요소를 고르면 오른쪽 편집기에 포커스를 넘겨 커서가 보이게 한다(2026-10-19 신고 「커서가 안 보임」 — 포커스가 왼쪽에
  // 남아 있어 편집기에 커서가 그려지지 않았다). 요청만 표시해 두고, 편집기가 그 요소의 글을 받은 뒤(렌더 후)에 옮긴다
  const editorRef = useRef(null);
  const focusEditorReq = useRef(false);

  const parsed = useMemo(() => parseSqlStructure(sql), [sql]);
  const index = useMemo(() => indexTree(parsed.root), [parsed]);
  const realErrors = parsed.errors.filter(e => e.code !== 'MULTI_STATEMENT');

  // 넘겨받은 SQL(편집기의 현재 문장·선택) — 새로 받으면 처음부터
  useEffect(() => {
    if (!seed) return;
    setSql(seed.sql || '');
    setHistory([]);
    setSelectedId('q0');
    setFocusRange(null);
    setHighlight([]);
    if (onSeedUsed) onSeedUsed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed && seed.nonce]);

  const selectInfo = useCallback((info) => {
    if (!info) return;
    setSelectedId(info.node.id);
    setFocusRange(rangeOf(info));
    setHighlight([{ from: info.node.start, to: info.node.end, type: 'table', label: info.node.id }]);
  }, []);

  // 다시 파싱한 뒤 — 방금 넣은 요소를 고른다. 고른 것이 사라졌으면 가장 가까운 윗 요소로
  useEffect(() => {
    if (pendingFocus.current != null) {
      const info = nodeStartingAt(index, pendingFocus.current);
      pendingFocus.current = null;
      if (info) { selectInfo(info); return; }
    }
    if (!index.has(selectedId)) {
      const parts = String(selectedId).split('/');
      while (parts.length > 1 && !index.has(parts.join('/'))) parts.pop();
      const info = index.get(parts.join('/')) || index.get('q0');
      if (info) selectInfo(info);
    } else if (!focusRange) {
      selectInfo(index.get(selectedId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const onSelect = useCallback((id) => {
    const info = index.get(id);
    focusEditorReq.current = true;
    selectInfo(info);
  }, [index, selectInfo]);

  const changeSql = useCallback((next, { record } = {}) => {
    if (record) setHistory(h => [...h.slice(-49), sql]);
    setSql(next);
  }, [sql]);

  // 왼쪽의 추가·삭제 — 원문의 한 자리를 고치고 다시 파싱(새 요소는 pendingFocus 로 고른다)
  const onAction = useCallback((action) => {
    const info = index.get(action.target);
    if (!info) return;
    const n = info.node;
    let r = null;
    const countKind = (clause, kind) => (clause ? clause.children.filter(c => c.kind === kind).length : 0);
    switch (action.op) {
      case 'addClause': r = addClause(sql, n, action.type); break;
      case 'addCte': {
        const q = info.type === 'query' ? n : info.parent;
        const w = q.clauses.find(c => c.type === 'WITH');
        r = addCte(sql, q, (w ? w.children.length : 0) + 1);
        break;
      }
      case 'addUnion': r = addUnion(sql, n); break;
      case 'addScalar': r = addScalar(sql, n); break;
      case 'addInline': r = addInline(sql, n, countKind(n, 'inline') + 1); break;
      case 'addCondition': r = addCondition(sql, info.parent, action.kind); break;
      case 'remove': r = removeNode(sql, n, info.parent, info.type); break;
      default: break;
    }
    if (!r) { toast.info(t('common:sqb.cannot')); return; }
    pendingFocus.current = action.op === 'remove' ? null : r.focus;
    focusEditorReq.current = action.op !== 'remove';
    if (action.op === 'remove') { setSelectedId(info.parent ? info.parent.id : 'q0'); setFocusRange(null); }
    setHighlight([]);   // 낡은 강조는 버린다 — 새 요소를 고르면 다시 잡는다
    changeSql(r.sql, { record: true });
  }, [index, sql, changeSql, t]);

  // 오른쪽 「선택 요소만」 편집 — 원래 자리에 끼우고, 범위는 직접 따라간다.
  // **SQLEditor 는 처음 마운트 때의 setQuery 를 계속 부른다**(편집기 확장을 한 번만 만든다). 그래서 상태에 기댄 함수를 주면
  // 낡은 사본(범위 없음)이 불려 전체 SQL 이 그 조각으로 덮였다(화면 확인 2026-10-19). 늘 같은 함수를 주고 최신 값은 ref 에서 읽는다 —
  // 타자가 빨라 렌더 전에 다음 글자가 와도 맞게, ref 는 고치는 즉시 갱신한다
  const live = useRef({ sql, focusRange });
  live.current = { sql, focusRange };
  const focusText = focusRange ? sql.slice(focusRange.start, focusRange.end) : sql;
  const onFocusEdit = useCallback((text) => {
    const { sql: cur, focusRange: fr } = live.current;
    if (!fr) { if (text !== cur) { live.current = { sql: text, focusRange: fr }; setSql(text); } return; }
    if (text === cur.slice(fr.start, fr.end)) return;
    const next = cur.slice(0, fr.start) + text + cur.slice(fr.end);
    const nextRange = { start: fr.start, end: fr.start + text.length };
    live.current = { sql: next, focusRange: nextRange };
    setSql(next);
    setFocusRange(nextRange);
  }, []);

  // 「전체」 에서 고칠 때는 선택·강조를 건드리지 않는다 — 다시 고르면 강조가 커서를 그 자리로 옮겨 타자가 튄다.
  // 선택 요소의 범위는 「선택 요소만」 으로 바꾸거나 다른 요소를 누를 때 다시 파싱한 것으로 새로 잡는다
  const onFullEdit = useCallback((text) => {
    if (text === live.current.sql) return;
    live.current = { ...live.current, sql: text };
    setSql(text);
  }, []);

  const switchMode = (m) => {
    setMode(m);
    focusEditorReq.current = true;
    const info = index.get(selectedId);
    if (info) selectInfo(info);
  };

  const undo = () => {
    if (!history.length) return;
    setSql(history[history.length - 1]);
    setHistory(history.slice(0, -1));
    setFocusRange(null);
    setHighlight([]);
  };

  // 편집기 포커스 — 「선택 요소만」 이면 그 글의 처음, 「전체」 면 그 요소의 시작에 커서
  useEffect(() => {
    if (!focusEditorReq.current) return undefined;
    const raf = requestAnimationFrame(() => {
      const view = editorRef.current;
      if (!view || !view.state || !focusEditorReq.current) return;
      focusEditorReq.current = false;
      const len = view.state.doc.length;
      const pos = mode === 'full' && highlight[0] ? Math.min(highlight[0].from, len) : 0;
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedId, focusRange, highlight, mode]);

  // 경로 — MAIN > V1 > 스칼라#2 (절이면 그 절까지)
  const crumbs = useMemo(() => {
    const out = [];
    let info = index.get(selectedId);
    while (info) {
      out.unshift(info.type === 'query' ? queryTitle(info.node, t) : (info.node.type === 'STATEMENT' ? t('common:sqb.statement') : info.node.type));
      info = info.parent ? index.get(info.parent.id) : null;
    }
    return out;
  }, [index, selectedId, t]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(sql); toast.success(t('common:sqb.copied')); } catch { toast.error(t('common:sqb.copyFailed')); }
  };
  const importFromEditor = () => {
    const got = onImport ? onImport() : null;
    if (!got || !got.sql) { toast.info(t('common:sqb.nothingToImport')); return; }
    if (sql.trim()) setHistory(h => [...h.slice(-49), sql]);
    setSql(got.sql);
    setSelectedId('q0');
    setFocusRange(null);
    setHighlight([]);
  };

  // 왼쪽 폭 끌기 — 분리 창에서도 그 창의 문서로(utils/dragListen), 마우스 거리는 화면 px 라 CSS px 로 바꾼다
  const startSplit = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = leftWidth;
    const max = bodyRef.current ? Math.max(LEFT_MIN, toCssPx(bodyRef.current.getBoundingClientRect().width) - 200) : 900;
    let w = w0;
    setSplitDrag(true);
    listenDrag(e, (ev) => {
      w = Math.round(Math.min(max, Math.max(LEFT_MIN, w0 + toCssPx(ev.clientX - x0))));
      setLeftWidth(w);
    }, () => {
      setSplitDrag(false);
      try { localStorage.setItem(LEFT_W_KEY, String(w)); } catch { /* 저장 못 해도 이번 창에는 남는다 */ }
    });
  };

  const errText = realErrors.length
    ? t(`common:sqb.err.${realErrors[0].code}`, { defaultValue: realErrors[0].code })
    : (parsed.errors.length ? t('common:sqb.err.MULTI_STATEMENT') : null);

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('common:sqb.title')} icon={<FiLayers size={14} />} className="app-modal-default sqb-modal"
      titleExtra={<EditorConnectionChip connections={connections} value={connectionId} />}>
      <div className="sqb-body" ref={bodyRef}>
        <section className="sqb-left" style={{ width: leftWidth }}>
          <div className="sqb-left-bar">
            <span className="sqb-left-title">{t('common:sqb.outline')}</span>
            <button type="button" className="app-btn" onClick={importFromEditor} title={t('common:sqb.importHint')}><FiDownload size={12} /> {t('common:sqb.import')}</button>
          </div>
          <div className="sqb-left-scroll">
            {sql.trim()
              ? <SqlOutline root={parsed.root} src={sql} selectedId={selectedId} onSelect={onSelect} onAction={onAction} dim={realErrors.length > 0} />
              : <div className="sqb-empty">{t('common:sqb.empty')}</div>}
          </div>
        </section>
        <div className={`sqb-split${splitDrag ? ' dragging' : ''}`} role="separator" aria-orientation="vertical"
          aria-label={t('common:sqb.resizeOutline')} title={t('common:sqb.resizeOutline')} onMouseDown={startSplit} />
        <section className="sqb-right">
          <div className="sqb-right-bar">
            <nav className="sqb-crumbs" aria-label={t('common:sqb.path')}>
              {crumbs.map((c, i) => <span key={`${c}-${i}`}>{i > 0 && <span className="sqb-crumb-sep">›</span>}{c}</span>)}
            </nav>
            <div className="sqb-seg" role="tablist" aria-label={t('common:sqb.viewMode')}>
              <button type="button" role="tab" aria-selected={mode === 'full'} className={mode === 'full' ? 'on' : ''} onClick={() => switchMode('full')} title={t('common:sqb.fullHint')}>{t('common:sqb.full')}</button>
              <button type="button" role="tab" aria-selected={mode === 'focus'} className={mode === 'focus' ? 'on' : ''} onClick={() => switchMode('focus')} title={t('common:sqb.focusHint')}>{t('common:sqb.focus')}</button>
            </div>
          </div>
          <div className="sqb-editor">
            <SQLEditor
              key={mode}
              editorRef={editorRef}
              query={mode === 'focus' ? focusText : sql}
              setQuery={mode === 'focus' ? onFocusEdit : onFullEdit}
              highlightRanges={mode === 'full' ? highlight : []}
              schemas={schemas}
              connectionId={connectionId}
              dbType={dbType}
              minimap={false}
              // 빌더 안에서는 실행하지 않는다(실행은 편집기에 반영한 뒤 편집기에서 — 두 번째 실행 경로를 만들지 않는다)
              onExecute={NOOP}
              onExecutePlan={NOOP}
              onViewSchemaInfoByEditor={NOOP}
            />
          </div>
          {errText && <div className="sqb-error" role="status">{errText}</div>}
        </section>
      </div>
      <footer className="sqb-foot">
        <button type="button" className="app-btn" onClick={undo} disabled={!history.length} title={t('common:sqb.undoHint')}><FiCornerUpLeft size={12} /> {t('common:sqb.undo')}</button>
        <span className="sqb-foot-gap" />
        <button type="button" className="app-btn" onClick={copy} disabled={!sql.trim()}><FiCopy size={12} /> {t('common:sqb.copy')}</button>
        <button type="button" className="app-btn" onClick={() => onNewTab && onNewTab(sql)} disabled={!sql.trim()}><FiFilePlus size={12} /> {t('common:sqb.newTab')}</button>
        <button type="button" className="app-btn app-btn-primary" onClick={() => onApply && onApply(sql)} disabled={!sql.trim()} title={t('common:sqb.applyHint')}><FiUpload size={12} /> {t('common:sqb.apply')}</button>
      </footer>
    </AppModal>
  );
}

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SqlBuilderModal);
